import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { useTheme, useMediaQuery, Box, Typography, CircularProgress,
    FormControl, MenuItem, InputLabel, Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { filterCrewRoutes } from 'state/helpers';
import { countCrewRoutesByField } from '../../charts/utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import RouteFilterDialog from 'components/RouteFilterDialog';
import PieChart from 'components/chartNivo/PieChart';
import RadialBarChart from 'components/chartNivo/RadialBarChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
    { value: "routesByDate", label: "by Date" },
    { value: "routesByPort", label: "by Port" },
    { value: "routesByDays", label: "by Days" },
    { value: "routesBySectors", label: "by Sectors" },
    { value: "routesByACType", label: "by AC Type" },
];

const CrewRouteCountChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    
    const [view, setView] = useState('routesByDate');
    
    const crewRoutesData = useSelector((state) => state.job255CrewRoutes);    
    
    const filteredCrewRoutes = useMemo(() => {
        return filterCrewRoutes({ crewRoutes: crewRoutesData, filters, timeMode });
    }, [crewRoutesData, filters, timeMode]);         
    
    const [data, setData] = useState({
        groupedRoutesByDate: [],
        routesByDate: [],
        barLinesRoutesByDate: [],
        
        crewRoutesByDep: [],
        routesByDep: [],
        barLinesRoutesByDep: [],

        crewRoutesByDays: [],
        radialRoutesByDays: [], 
        groupedRoutesByDays: [],
        routesByDays: [],
        barLinesRoutesByDays: [],

        crewRoutesBySectors: [],
        radialRoutesBySectors: [], 
        groupedRoutesBySectors: [],
        routesBySectors: [],
        barLinesRoutesBySectors: [],

        crewRoutesByACType: [], 
        radialRoutesByACType: [], 
        groupedRoutesByACType: [],
        routesByACType: [],
        barLinesRoutesByACType: [],
    });

    useEffect(() => {  
        const handleData = async () => {
            try {
                const results = {
                    groupedRoutesByDate: countCrewRoutesByField(filteredCrewRoutes, timeMode === 'UTC' ? 'routeDate' : 'routeDateLocalBase', 'groupedColumn'),
                    routesByDate: countCrewRoutesByField(filteredCrewRoutes, timeMode === 'UTC' ? 'routeDate' : 'routeDateLocalBase', 'column'),
                    barLinesRoutesByDate: countCrewRoutesByField(filteredCrewRoutes, timeMode === 'UTC' ? 'routeDate' : 'routeDateLocalBase', 'barlines'),
                    
                    crewRoutesByDep: countCrewRoutesByField(filteredCrewRoutes, 'dep', 'pie'),
                    routesByDep: countCrewRoutesByField(filteredCrewRoutes, 'dep', 'column'),
                    barLinesRoutesByDep: countCrewRoutesByField(filteredCrewRoutes, 'dep', 'barlines'),
                    
                    crewRoutesByDays: countCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'pie'),
                    radialRoutesByDays: countCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'radialbar'),
                    groupedRoutesByDays: countCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'groupedColumn'),
                    routesByDays: countCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'column'),
                    barLinesRoutesByDays: countCrewRoutesByField(filteredCrewRoutes, 'routeDays', 'barlines'),
                    
                    crewRoutesBySectors: countCrewRoutesByField(filteredCrewRoutes, 'sectors', 'pie'),   
                    radialRoutesBySectors: countCrewRoutesByField(filteredCrewRoutes, 'sectors', 'radialbar'),
                    groupedRoutesBySectors: countCrewRoutesByField(filteredCrewRoutes, 'sectors', 'groupedColumn'),
                    routesBySectors: countCrewRoutesByField(filteredCrewRoutes, 'sectors', 'column'),
                    barLinesRoutesBySectors: countCrewRoutesByField(filteredCrewRoutes, 'sectors', 'barlines'),
                    
                    crewRoutesByACType: countCrewRoutesByField(filteredCrewRoutes, 'acType', 'pie'), 
                    radialRoutesByACType: countCrewRoutesByField(filteredCrewRoutes, 'acType', 'radialbar'),
                    groupedRoutesByACType: countCrewRoutesByField(filteredCrewRoutes, 'acType', 'groupedColumn'),
                    routesByACType: countCrewRoutesByField(filteredCrewRoutes, 'acType', 'column'),
                    barLinesRoutesByACType: countCrewRoutesByField(filteredCrewRoutes, 'acType', 'barlines'),
                };
                
                setData(results); 
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false); 
            }
        };

        handleData();        
    }, [timeMode, filteredCrewRoutes]); 
        
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const buildOptions = (data, key) =>
        [...new Set(data.map(item => item[key]))].sort();

    const { 
        acTypeOptions, 
        flightRegOptions, 
        depOptions, 
        arrOptions 
    } = useMemo(() => ({
        acTypeOptions: buildOptions(crewRoutesData, 'acType'),
        flightRegOptions: buildOptions(crewRoutesData, 'flightReg'),
        depOptions: buildOptions(crewRoutesData, 'dep'),
        arrOptions: buildOptions(crewRoutesData, 'arr'),
    }), [crewRoutesData]);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Routes ${period}`;

    const chartMap = {
        routesByDate: [
            { component: <SortBarChart data={data.groupedRoutesByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Count" digit={0} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByDate} title={defaultTitle} bottomLegend='Date' leftLegend='Value' digit={0} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
        routesByPort: [
            { component: <PieChart data={data.crewRoutesByDep} title={defaultTitle} itemWidthOffSet={-35} />, style: {} },
            { component: <SortBarChart data={data.routesByDep} title={defaultTitle} bottomLegend="Port" leftLegend="Count" digit={0} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Value' digit={0} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
        routesByDays: [
            { component: <PieChart data={data.crewRoutesByDays} title={defaultTitle} itemWidthOffSet={-60} />, style: {} },
            { component: <RadialBarChart data={data.radialRoutesByDays} title={defaultTitle} itemWidthOffSet={-35}  />, style: {} },
            { component: <SortBarChart data={data.groupedRoutesByDays} title={defaultTitle} bottomLegend="Days" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesByDays} title={defaultTitle} bottomLegend="Days" leftLegend="Count" isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByDays} title={defaultTitle} bottomLegend='Days' leftLegend='Value' digit={0} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
        routesBySectors: [
            { component: <PieChart data={data.crewRoutesBySectors} title={defaultTitle} itemWidthOffSet={-60} />, style: {} },
            { component: <RadialBarChart data={data.radialRoutesBySectors} title={defaultTitle} itemWidthOffSet={-35}  />, style: {} },
            { component: <SortBarChart data={data.groupedRoutesBySectors} title={defaultTitle} bottomLegend="Sectors" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesBySectors} title={defaultTitle} bottomLegend="Sectors" leftLegend="Count" isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesBySectors} title={defaultTitle} bottomLegend='Sectors' leftLegend='Value' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
        routesByACType: [
            { component: <PieChart data={data.crewRoutesByACType} title={defaultTitle} itemWidthOffSet={-40} />, style: {} },
            { component: <RadialBarChart data={data.radialRoutesByACType} title={defaultTitle} itemWidthOffSet={-35}  />, style: {} },
            { component: <SortBarChart data={data.groupedRoutesByACType} title={defaultTitle} bottomLegend="AC Type" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-10} />, style: {} },
            { component: <SortBarChart data={data.routesByACType} title={defaultTitle} bottomLegend="AC Type" leftLegend="Count" isShowAvgLine={false} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.barLinesRoutesByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Value' digit={0} barTranslateXOffSet={-35} lineTranslateXOffSet={20} itemWidthOffSet={20} />, style: {} }, 
        ],
    };

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header 
                    title="Route (Job 255)" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '8rem', sm: '9rem' } }}
                        >
                            <InputLabel id="routes-view-label">View</InputLabel>
                            <Select
                                labelId="routes-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">Error: {error.message}</Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                >
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
       
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}  

            {isFilterDialogOpen &&
                <RouteFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    depOptions={depOptions}
                    arrOptions={arrOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default CrewRouteCountChart;
